/**
 * Colour tokens from the Figma design system.
 *
 * Semantic role groups (background, text, stroke, …) come from the
 * "Semantics: Color" collection. The hue groups (mossGreen, cerulean, …)
 * are named values from the Figma "Colours" palette collection, used where a
 * fill needs a specific hue rather than a semantic role. Both mirror Figma's
 * slash groups as nested objects per ADR-010 Rule 4.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

export const iconToneNames = [
  'primary',
  'secondary',
  'tertiary',
  'disabled',
  'inverted',
  'highlight',
  'insight',
  'success',
] as const;
export type IconToneName = (typeof iconToneNames)[number];

export const colors = {
  background: {
    primary: '#f2f2f2',
    secondary: '#e3e3e3',
    tertiary: '#cccccc',
    dark: '#222222',
    container: '#ffffff',
    callout: '#deebfe',
    highlight: '#b2d3fc',
    success: '#89c63f',
    successLight: '#d4e8b3',
    overlay: 'rgba(0, 0, 0, 0.4)',
    light: '#ffffff',
    gradient80: '#f2f2f2cc',
    gradientTransparent: '#f2f2f200',
    // Figma `Secondary/Secondary-default` — the brand yellow used as the
    // full-bleed welcome hero. Named `brand` (not `secondary`) so it doesn't
    // collide with `button.secondary` (#e3e3e3); the comment records the Figma
    // source per the `text.accent` precedent above (ADR-010 Rule 4).
    brand: '#fae355',
    // Figma `Background/Background-mistblue` — the soft periwinkle surface
    // behind conversational speech bubbles (e.g. the Sona-intro bubble).
    mistblue: '#e8eeff',
    // Figma `Background/Background-lighttan` — the neutral illustration field
    // used by review and reflection media frames.
    lightTan: '#ded4c7',
  },
  text: {
    black: '#000000',
    primary: '#222222',
    // Alpha variant of text/primary. Inline React Native text must animate its
    // foreground color (not view opacity) to remain hidden on Android Spannable.
    primaryTransparent: '#22222200',
    secondary: '#5a5a5a',
    tertiary: '#848484',
    disabled: '#cccccc',
    inverted: '#ffffff',
    alert: '#d02f15',
    success: '#4d6525',
    highlight: '#5e7a9a',
    highlightInverted: '#b2d3fc',
    insight: '#48536f',
    // Figma "text/highlight" on light surfaces (eyebrow/labels). A distinct,
    // darker blue-grey than `highlight` (#5e7a9a) despite sharing the Figma name.
    accent: '#415771',
    // Figma `Text/Text-global-primary` (== `Primary/Primary-default`) — the
    // near-black used for the welcome heading/paragraph. A hair cooler than the
    // `primary` near-black, so it gets its own token rather than reusing it.
    global: '#212633',
  },
  stroke: {
    primary: '#222222',
    secondary: '#5a5a5a',
    tertiary: '#aaaaaa',
    divider: '#e3e3e3',
    callout: '#b2d3fc',
    success: '#a2cc6e',
    // Figma `stroke/alert` — the outline of an element in an error or
    // wrong-answer state. The same red as `button.critical`.
    alert: '#f04438',
    container: '#ffffff',
    // Form field border. `divider` (#e3e3e3) is about 1.3:1 on a white field,
    // below WCAG 1.4.11's 3:1 for a control's boundary; this is about 3.7:1.
    // Same hex as `text.tertiary`, but a border does not borrow a text role.
    // Named after `stroke/input` on the web component sheet (Figma
    // hk9NaJrKRwkoGmmZ5rxbsE, page v7); it has no UI Design System library
    // name yet, so the key may be renamed once design names it there.
    input: '#848484',
  },
  button: {
    primary: '#222222',
    primaryPressed: '#000000',
    // Pointer-hover fill for the primary button on web; native has no hover.
    // Figma `Colours/neutral/800` on the web component sheet (hk9NaJrKRwkoGmmZ5rxbsE,
    // page v7), which has no UI Design System library name yet.
    primaryHover: '#3b3b3b',
    // Figma `misc/variation-p` — dark plum primary CTA (care booking, week-strip
    // selection, and other brand-filled controls). Same hex as `misc.variationP`.
    brand: '#2e131d',
    brandPressed: '#1a0810',
    secondary: '#e3e3e3',
    critical: '#f04438',
    disabled: '#cccccc',
    inactive: '#415771',
  },
  icon: {
    primary: '#222222',
    secondary: '#5a5a5a',
    tertiary: '#848484',
    disabled: '#cccccc',
    inverted: '#ffffff',
    highlight: '#5e7a9a',
    insight: '#732f33',
    // Same green as `background.success` — the positive/complete icon tint.
    success: '#89c63f',
  } satisfies Record<IconToneName, string>,
  avatar: {
    background: '#a4bdff',
    accent: '#e8eeff',
  },
  nav: {
    gradientStart: 'rgba(255, 255, 255, 0)',
    gradientEnd: 'rgba(255, 255, 255, 0.6)',
    pillOverlay: 'rgba(255, 255, 255, 0.35)',
    pillBorder: 'rgba(0, 0, 0, 0.12)',
    highlight: 'rgba(0, 0, 0, 0.08)',
  },
  // Translucent white laid over a tinted card: the dismiss control's disc and
  // the hairline rule between a tinted card's header and body.
  onTint: {
    control: 'rgba(255, 255, 255, 0.7)',
    rule: 'rgba(255, 255, 255, 0.75)',
  },
  // Dark scrim gradient laid over full-bleed video so inverted text/controls
  // stay legible (Figma in-call frame) — the dark counterpart of the light
  // `nav.gradientStart/gradientEnd` pair.
  scrim: {
    gradientStart: 'rgba(0, 0, 0, 0)',
    gradientEnd: 'rgba(0, 0, 0, 0.6)',
  },
  shadow: {
    shadow: 'rgba(33, 38, 51, 0.08)',
  },
  // Named hues from the Figma "Colours" palette collection (more saturated than
  // the success/highlight semantic tints). Shade keys mirror Figma's slash group.
  mossGreen: {
    /** CAB 2026 pale appointment-media fill. */
    '300': '#ebf7dc',
    '500': '#c9e8a3',
    '900': '#2d430e',
  },
  cerulean: {
    '200': '#e8eeff',
    '400': '#bed0ff',
    // Figma `Colours/cerulean/600` — the periwinkle dot marking the "Ask Sona"
    // section heading.
    '600': '#8195c9',
    '800': '#48536f',
  },
  punchPink: {
    '200': '#f9e8fe',
    // Figma `Colours/punchPink/500` — the pink/lavender accent behind the
    // rewards "available to redeem" / "redeem for" pills. Value matches the
    // existing `misc/variation-f` (same Figma colour, canonical name here).
    '500': '#eebcff',
  },
  coral: {
    '100': '#fff4dc',
  },
  // Figma `Colours/yellow` — the home's high-contrast card. `800` is the rule
  // drawn between its rows: the semantic `stroke.divider` (#e3e3e3) is a
  // near-invisible light grey on this fill, so the design pins a darker tone.
  yellow: {
    '400': '#fbe977',
    '800': '#7c7023',
  },
  // Figma `Colours/denim/100` — pale blue panel behind the gift-card reward
  // illustration (ADR-010 Rule 4).
  denim: {
    '100': '#f5f7ff',
    // Figma `Colours/denim/700` — the chart-mark blue-grey. Shares its value with
    // the `text.highlight` / `icon.highlight` semantic aliases, but a plotted bar
    // is neither text nor an icon, so it reads the hue-group token by its Figma
    // name rather than borrowing a semantic role that does not apply.
    '700': '#5e7a9a',
  },
  // Named values from the Figma "misc" collection — specific brand hues used as
  // fills where no semantic role token fits. `variationP` is the dark plum
  // surface for the selected/active Date chip in the WeekStrip "Calendar";
  // `variationN` is the light neutral surface marking the "today" chip.
  // Mirror the Figma slash groups (`misc/variation-p`, `misc/variation-n`) per
  // ADR-010 Rule 4.
  misc: {
    variationP: '#2e131d',
    variationN: '#efedea',
    // Figma `misc/variation-f` — the pink/lavender accent used by the streak
    // bolt glyph (ADR-010 Rule 4).
    variationF: '#eebcff',
    // Figma `misc/variation-j` — the active-value accent on the NumberPicker's
    // inline field/reference tile.
    variationJ: '#a4bdff',
    /** Figma `misc/variation-s` — strong blue media fill with inverted glyphs. */
    variationS: '#65759d',
    /** Figma `misc/variation-m` — purple accent bar (its first consumer, the guided-conversations intro graphic, was retired; the token stays in the vocabulary). */
    variationM: '#c88cdc',
    /** Figma `misc/variation-q` — the lilac badge fill on the home Mood summary card. */
    variationQ: '#f7dffe',
    /**
     * Figma `misc/variation-b` — the cream badge fill on the home surrounding-tags
     * summary card. Value matches the existing `coral['100']` (same colour, that
     * is the canonical name here), kept under its Figma slash name for the same
     * reason `punchPink['500']` / `misc/variation-f` are both spelled out.
     */
    variationB: '#fff4dc',
    /**
     * Figma `misc/variation-d` — the light-green programme pill on the home
     * past-day free-day card. Named for the Figma slash group rather than a
     * semantic role, for the same reason as its siblings here: it is a decorative
     * fill with no role token that fits.
     */
    variationD: '#c9e8a3',
  },
} as const;

export type Colors = typeof colors;
